import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, RefreshCw, CheckCircle2, CalendarDays, Building2, Zap } from "lucide-react";

export default function CalendarSync() {
  const [data, setData] = useState(null);
  const load = () => api.get("/telehealth/integrations").then(({ data }) => setData(data)).catch(() => setData({ integrations: [] }));
  useEffect(() => { load(); }, []);
  const request = async (key) => {
    try { await api.post(`/telehealth/integrations/${key}/request-access`); toast.success("Early access requested — we'll notify you when this sync goes live."); load(); }
    catch { toast.error("Could not submit request"); }
  };
  if (!data) return <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="th-calendar-sync">
      <div className="rounded-3xl bg-navy text-white p-6 mb-5">
        <div className="flex items-center gap-2 text-cobalt text-xs font-bold uppercase tracking-wide"><RefreshCw className="w-4 h-4" /> Intelligent Two-Way Scheduling</div>
        <h2 className="font-heading text-xl font-bold mt-2">One calendar. Zero double entry.</h2>
        <p className="text-sm text-white/70 mt-1 max-w-2xl">Crash Ready doesn't replace your scheduling system — it makes it smarter. Soon, appointments added, rescheduled or cancelled anywhere (your EHR, practice manager or calendar) will sync availability here automatically — and Crash Ready appointments will flow straight back into your office calendar.</p>
      </div>

      <div className="rounded-3xl bg-white border border-emerald-200 p-6 mb-5" data-testid="th-engine-live">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-500" />
          <h3 className="font-heading font-bold text-navy">{data.engine?.name}</h3>
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Live now</span>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {(data.engine?.capabilities || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {c}</div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">External syncs below plug directly into this engine — your rules and conflict detection stay identical when integrations go live.</p>
      </div>

      <h3 className="font-heading font-semibold text-navy mb-3">Practice Management & Calendar Integrations</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(data.integrations || []).map((i) => (
          <div key={i.key} className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col" data-testid={`th-integration-${i.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {i.category === "Calendar" ? <CalendarDays className="w-4 h-4 text-cobalt" /> : <Building2 className="w-4 h-4 text-cobalt" />}
                <span className="font-semibold text-navy text-sm">{i.name}</span>
              </div>
              <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Coming soon</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-1">{i.category}</div>
            <button onClick={() => request(i.key)} disabled={i.early_access_requested} data-testid={`th-request-access-${i.key}`}
              className={`mt-3 px-3 py-1.5 rounded-full text-[11px] font-semibold ${i.early_access_requested ? "bg-emerald-50 text-emerald-600 cursor-default" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {i.early_access_requested ? "✓ Early access requested" : "Request Early Access"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
